import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { Sim } from "./App";
import { calculate, processIntent, type CommitEvent } from "./Tasks.ts";
import { onboardingTasks } from "./data.js";
import "@/styles/Splash.css";
import { Card, CardContent } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { GitBranch, ListTree, Ban, Sparkles, Quote } from "lucide-react";

export default function Splash() {
  return (
      // <article className="mx-auto max-w-5xl px-6 py-20 flex flex-col items-center text-center gap-6">
      <article className="bg-gradient-to-b from-background via-background to-secondary/10 splash">

        <h1 className="title text-5xl md:text-6xl font-bold tracking-tight"> Zeno </h1>
        <blockquote>
          "That which is in locomotion must arrive at the half-way stage before it arrives at the goal"
        </blockquote>

        <p className="max-w-2xl text-lg" style={{textAlign:'left'}}>
          Zeno of Elea argued that movement was logically impossible, because 
          every step can be broken down into an infinite series of smaller steps
          which must take an infinite amount of time to complete.

          Zeno will force you to name what stands between you and your goals.
          Completing them will still be up to you.
        </p>

        <HeroGraph />

        <p className="text-xs text-muted-foreground">
          Live preview &mdash; drag nodes around, this is a real tree.
        </p>

        <div className="flex flex-wrap items-center justify-center gap-3 mt-2">
          <Link to="/sandbox"><Button size="lg">Try the sandbox</Button></Link>
          <Link to="/signup"><Button size="lg" variant="outline">Sign up</Button></Link>
          <Link to="/login"><Button size="lg" variant="ghost">Log in</Button></Link>
        </div>

        <div className="grid gap-4 sm:grid-cols-3 mt-16 w-full text-left">
          <Card>
            <CardContent className="flex flex-col gap-2">
              <ListTree className="text-[#599959]" />
              <h3 className="font-semibold">Trees, not lists</h3>
              <p className="text-sm text-muted-foreground">
                Lay tasks out as a force-directed graph so dependencies are
                visible at a glance, not buried in nested checklists.
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex flex-col gap-2">
              <Ban className="text-[#599959]" />
              <h3 className="font-semibold">See what's blocking you</h3>
              <p className="text-sm text-muted-foreground">
                Instantly spot which tasks are actionable now and which are
                waiting on something else to finish first.
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex flex-col gap-2">
              <GitBranch className="text-[#599959]" />
              <h3 className="font-semibold">Built for real goals</h3>
              <p className="text-sm text-muted-foreground">
                Big goals branch into many small tasks. Task Tree mirrors
                that structure instead of flattening it away.
              </p>
            </CardContent>
          </Card>
        </div>

        <p className="text-xs text-muted-foreground mt-16">
          No account needed to look around &mdash; the sandbox loads a sample tree
          you can explore and rearrange.
        </p>
      </article>
  )
}

function HeroGraph() {
  const [tasks, setTasks] = useState(() => onboardingTasks['snapshot']);
  const [selectedTaskID, setSelectedTaskID] = useState<string>();
  const [, setHoveredTaskID] = useState<string>();
  const [addDependencyTaskID, setAddDependencyTaskID] = useState<string>();

  const solvedTasks = useMemo(() => calculate(tasks), [tasks]);

  const handleCommits = useCallback((events: CommitEvent[]) => {
    setTasks(prev => {
      let updated = calculate(prev);
      for (const e of events) {
        updated = processIntent(e, updated);
      }
      return updated;
    })
  }, [])

  const vizConfig = useMemo(() => ({
    labelFilter: { complete: true, blocked: true, external: true, resolved: true },
    taskFilter: { complete: true, blocked: true, external: true, resolved: false },
    dateLabelFormat: 'OFFSET' as const,
    futureDays: 0
  }), [])

  return (
    <div className="heroviz wide">
      <Sim tasks={solvedTasks}
        selectTask={setSelectedTaskID}
        hoverTask={setHoveredTaskID}
        onCommit={handleCommits}
        selectedTask={selectedTaskID}
        addDependencyTask={addDependencyTaskID}
        setAddDependencyTask={setAddDependencyTaskID}
        vizConfig={vizConfig}
      />
    </div>
  )
}
